import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { 
  Grid, 
  Checkbox, 
  FormControlLabel, 
  TextField, 
  Button, 
  Autocomplete, 
  Slider,
  Typography
} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import PriceDisplay from "components/PriceDisplay";
import gameServerService from "services/gameServerService";
import Swal from "sweetalert2";
import { AuthContext } from "context";

function GameServerConfig({ onBack }) {
  const { user } = useContext(AuthContext);
  const [ram, setRam] = useState(4); // Default: 4 GB RAM
  const [storage, setStorage] = useState(50); // Default: 50 GB Storage
  const [cores, setCores] = useState(2); // Default: 2 Cores
  const [instances, setInstances] = useState(1); // Default: 1 Instanz
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [originalPrice, setOriginalPrice] = useState(null);
  const [discountApplied, setDiscountApplied] = useState(false);

  // Notification functions
  const showSuccessNotification = (message) => {
    Swal.fire({
      icon: 'success',
      title: 'Erfolg!',
      text: message,
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'OK'
    });
  };

  const showErrorNotification = (message) => {
    Swal.fire({
      icon: 'error',
      title: 'Fehler',
      text: message,
      confirmButtonColor: '#d33',
      confirmButtonText: 'OK'
    });
  };

  const showWarningNotification = (message) => {
    Swal.fire({
      icon: 'warning',
      title: 'Achtung',
      text: message,
      confirmButtonColor: '#f8bb86',
      confirmButtonText: 'OK'
    });
  };

  // Berechne den Preis basierend auf den ausgewählten Ressourcen
  const calculatePrice = () => {
    try {
      const basePrice = gameServerService.calculatePrice(ram, storage, cores, instances);
      
      const isPartner = user?.isPartner === true;

      if (isPartner) {
        // 20% Rabatt für Partner
        setOriginalPrice(basePrice);
        setTotalPrice(basePrice * 0.8);
        setDiscountApplied(true);
      } else {
        setTotalPrice(basePrice);
        setOriginalPrice(null);
        setDiscountApplied(false);
      }
      
      return isPartner ? basePrice * 0.8 : basePrice;
    } catch (error) {
      console.error("Fehler bei der Preisberechnung:", error);
      return 0;
    }
  };

  // Automatische Preisberechnung, wenn sich die Ressourcen ändern
  useEffect(() => {
    calculatePrice();
  }, [ram, storage, cores, instances, user?.isPartner]);

  const handleOrder = async () => {
    if (agreedToTerms) {
      const userId = user?.id;
      
      const orderDetails = {
        userId,
        ram,
        storage,
        cores,
        instances,
        totalPrice,
      };
      
      // Füge Rabattinformationen hinzu, wenn ein Rabatt angewendet wurde
      if (discountApplied) {
        orderDetails.priceInfo = {
          originalPrice: originalPrice,
          discountApplied: true
        };
      }

      try {
        const response = await gameServerService.orderGameServer(orderDetails);

        if (response.status === 200) {
          showSuccessNotification("Ihre Game Server Bestellung wurde erfolgreich aufgegeben!");
          setTimeout(() => {
            window.location.href = "/iaas/server";
          }, 2000);
        } else {
          console.error("Unerwartete Antwort:", response);
          showErrorNotification("Es gab ein Problem bei der Bestellung. Bitte versuchen Sie es später erneut.");
        }
      } catch (error) {
        console.error("Fehler bei der Bestellung:", error);
        showErrorNotification("Es gab ein Problem bei der Bestellung. Bitte versuchen Sie es später erneut.");
      }
    } else {
      showWarningNotification("Bitte stimmen Sie den AGBs zu, um fortzufahren.");
    }
  };

  return (
    <SoftBox p={2}>
      <SoftTypography variant="h4" mb={2}>
        UDS Game Server Konfiguration
      </SoftTypography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <SoftBox px={2} py={1} border="1px solid #e9ecef" borderRadius="lg" mb={2}>
            <SoftTypography variant="h6" mb={1}>RAM (GB)</SoftTypography>
            <SoftBox px={2}>
              <Slider
                value={ram}
                min={2}
                max={64}
                step={2}
                onChange={(e, newValue) => setRam(newValue)}
                valueLabelDisplay="auto"
                aria-labelledby="ram-slider"
                marks={[
                  { value: 2, label: '2 GB' },
                  { value: 16, label: '16 GB' },
                  { value: 32, label: '32 GB' },
                  { value: 64, label: '64 GB' }
                ]}
              />
            </SoftBox>
            <Typography variant="body2" color="text.secondary" align="right" mt={1}>
              Ausgewählt: {ram} GB - Preis: {ram * 1.25} €
            </Typography>
          </SoftBox>
        </Grid>

        <Grid item xs={12}>
          <SoftBox px={2} py={1} border="1px solid #e9ecef" borderRadius="lg" mb={2}>
            <SoftTypography variant="h6" mb={1}>Speicherplatz (GB)</SoftTypography>
            <SoftBox px={2}>
              <Slider
                value={storage}
                min={25}
                max={500}
                step={25}
                onChange={(e, newValue) => setStorage(newValue)}
                valueLabelDisplay="auto"
                aria-labelledby="storage-slider"
                marks={[
                  { value: 25, label: '25 GB' },
                  { value: 100, label: '100 GB' },
                  { value: 250, label: '250 GB' },
                  { value: 500, label: '500 GB' }
                ]}
              />
            </SoftBox>
            <Typography variant="body2" color="text.secondary" align="right" mt={1}>
              Ausgewählt: {storage} GB - Preis: {(storage * 0.1).toFixed(2)} €
            </Typography>
          </SoftBox>
        </Grid>

        <Grid item xs={12}>
          <SoftBox px={2} py={1} border="1px solid #e9ecef" borderRadius="lg" mb={2}>
            <SoftTypography variant="h6" mb={1}>CPU-Kerne</SoftTypography>
            <SoftBox px={2}>
              <Slider
                value={cores}
                min={1}
                max={16}
                step={1}
                onChange={(e, newValue) => setCores(newValue)}
                valueLabelDisplay="auto"
                aria-labelledby="cores-slider"
                marks={[
                  { value: 1, label: '1 Kern' },
                  { value: 4, label: '4 Kerne' },
                  { value: 8, label: '8 Kerne' },
                  { value: 16, label: '16 Kerne' }
                ]}
              />
            </SoftBox>
            <Typography variant="body2" color="text.secondary" align="right" mt={1}>
              Ausgewählt: {cores} Kerne - Preis: {cores * 1} €
            </Typography>
          </SoftBox>
        </Grid>

        <Grid item xs={12}>
          <SoftBox px={2} py={1} border="1px solid #e9ecef" borderRadius="lg" mb={2}>
            <SoftTypography variant="h6" mb={1}>Gameserver Instanzen</SoftTypography>
            <SoftBox px={2}>
              <Slider
                value={instances}
                min={1}
                max={10}
                step={1}
                onChange={(e, newValue) => setInstances(newValue)}
                valueLabelDisplay="auto"
                aria-labelledby="instances-slider"
                marks={[
                  { value: 1, label: '1 Instanz' },
                  { value: 3, label: '3 Instanzen' },
                  { value: 5, label: '5 Instanzen' },
                  { value: 10, label: '10 Instanzen' }
                ]}
              />
            </SoftBox>
            <Typography variant="body2" color="text.secondary" align="right" mt={1}>
              Ausgewählt: {instances} Instanz{instances > 1 ? 'en' : ''} - Preis: {instances * 1.5} €
            </Typography>
          </SoftBox>
        </Grid>

        <Grid item xs={12}>
          <SoftBox p={2} border="1px solid #e9ecef" borderRadius="lg" mb={2}>
            <SoftTypography variant="h5" fontWeight="bold" mb={2}>
              <PriceDisplay 
                price={totalPrice} 
                originalPrice={originalPrice} 
                discountApplied={discountApplied} 
                title="Gesamtpreis:"
              />
            </SoftTypography>
          </SoftBox>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
              />
            }
            label={
              <SoftTypography variant="body2">
                Ich akzeptiere die{" "}
                <a href="/agb" target="_blank" rel="noopener noreferrer">
                  AGBs
                </a>{" "}
                und bestelle den UDS Game Server kostenpflichtig.
              </SoftTypography>
            }
          />
        </Grid>

        <Grid item xs={12}>
          <SoftBox display="flex" justifyContent="space-between">
            <Button variant="contained" color="secondary" onClick={onBack}>
              Zurück
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOrder}
              disabled={!agreedToTerms}
            >
              Jetzt bestellen
            </Button>
          </SoftBox>
        </Grid>
      </Grid>
    </SoftBox>
  );
}

GameServerConfig.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default GameServerConfig;